import { useLocation } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { siteConfig } from '@/config/site';

interface BreadcrumbItem {
  name: string;
  url: string;
}

interface ArticleData {
  title: string;
  description: string;
  image?: string;
  datePublished?: string;
  dateModified?: string;
  author?: string;
}

interface StructuredDataProps {
  type?: 'website' | 'article' | 'profile' | 'organization';
  breadcrumbs?: BreadcrumbItem[];
  article?: ArticleData;
  /** Pass page title & description so we don't read document.title */
  pageTitle?: string;
  pageDescription?: string;
}

/**
 * Declarative Schema.org structured data component.
 * Returns a <script type="application/ld+json"> tag — NO useEffect, NO document access.
 */
export function StructuredData({ type = 'website', breadcrumbs, article, pageTitle, pageDescription }: StructuredDataProps) {
  const location = useLocation();
  const { i18n } = useTranslation();
  const currentLang = i18n.language === 'zh' ? 'zh-CN' : 'en';
  const baseUrl = siteConfig.url;
  const currentUrl = `${baseUrl}${location.pathname}`;

  // 1. Organization Schema
  const organizationSchema = {
    '@type': 'Organization',
    '@id': `${baseUrl}/#organization`,
    name: 'borninsea',
    alternateName: '海生工作室',
    url: baseUrl,
    logo: {
      '@type': 'ImageObject',
      url: `${baseUrl}/logo.png`,
      width: 512,
      height: 512,
    },
    image: siteConfig.seo.ogImage,
    description: currentLang === 'zh-CN'
      ? '专注于AI创业与创伤疗愈探索的个人工作室'
      : 'Personal studio focused on AI entrepreneurship and trauma healing exploration',
    founder: { '@type': 'Person', '@id': `${baseUrl}/#person` },
    foundingDate: '2024',
    sameAs: [
      siteConfig.social.twitter.url,
      siteConfig.social.github.url,
      siteConfig.social.linkedin.url,
      siteConfig.social.youtube.url,
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: siteConfig.contact.methods.email,
      availableLanguage: ['Chinese', 'English'],
    },
  };

  // 2. Person Schema
  const personSchema = {
    '@type': 'Person',
    '@id': `${baseUrl}/#person`,
    name: siteConfig.author.name,
    alternateName: [siteConfig.author.nameEn, 'borninsea'],
    url: `${baseUrl}/about`,
    image: `${baseUrl}/og-image.png`,
    description: currentLang === 'zh-CN' ? siteConfig.author.bio : siteConfig.author.bioEn,
    jobTitle: currentLang === 'zh-CN'
      ? ['产品经理', 'AI创业者', '创伤探索者']
      : ['Product Manager', 'AI Entrepreneur', 'Trauma Explorer'],
    worksFor: { '@type': 'Organization', '@id': `${baseUrl}/#organization` },
    alumniOf: {
      '@type': 'EducationalOrganization',
      name: currentLang === 'zh-CN' ? '电子科技大学' : 'University of Electronic Science and Technology of China',
      alternateName: 'UESTC',
    },
    knowsAbout: currentLang === 'zh-CN'
      ? ['人工智能', '医疗科技', '产品管理', '创伤疗愈', '创业', '心理健康']
      : ['Artificial Intelligence', 'HealthTech', 'Product Management', 'Trauma Healing', 'Entrepreneurship', 'Mental Health'],
    sameAs: [
      siteConfig.social.twitter.url,
      siteConfig.social.github.url,
      siteConfig.social.linkedin.url,
      siteConfig.social.youtube.url,
    ],
    email: siteConfig.contact.methods.email,
    nationality: { '@type': 'Country', name: 'China' },
  };

  // 3. WebSite Schema
  const webSiteSchema = {
    '@type': 'WebSite',
    '@id': `${baseUrl}/#website`,
    name: siteConfig.name,
    alternateName: currentLang === 'zh-CN' ? '王海生个人网站' : 'Wang Haisheng Personal Website',
    url: baseUrl,
    description: siteConfig.seo.defaultDescription,
    inLanguage: [currentLang, currentLang === 'zh-CN' ? 'en' : 'zh-CN'],
    publisher: { '@type': 'Organization', '@id': `${baseUrl}/#organization` },
    author: { '@type': 'Person', '@id': `${baseUrl}/#person` },
    potentialAction: {
      '@type': 'SearchAction',
      target: { '@type': 'EntryPoint', urlTemplate: `${baseUrl}/blog?search={search_term_string}` },
      'query-input': 'required name=search_term_string',
    },
  };

  // 4. WebPage Schema
  const webPageSchema = {
    '@type': type === 'profile' ? 'ProfilePage' : 'WebPage',
    '@id': `${currentUrl}#webpage`,
    url: currentUrl,
    name: pageTitle || '',
    description: pageDescription || '',
    isPartOf: { '@type': 'WebSite', '@id': `${baseUrl}/#website` },
    about: { '@type': 'Person', '@id': `${baseUrl}/#person` },
    inLanguage: currentLang,
    datePublished: article?.datePublished,
    dateModified: article?.dateModified || new Date().toISOString().split('T')[0],
  };

  // 5. BreadcrumbList Schema
  const breadcrumbSchema = breadcrumbs && breadcrumbs.length > 0 ? {
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url.startsWith('http') ? item.url : `${baseUrl}${item.url}`,
    })),
  } : null;

  // 6. Article Schema
  const articleSchema = type === 'article' && article ? {
    '@type': 'Article',
    '@id': `${currentUrl}#article`,
    headline: article.title,
    description: article.description,
    image: article.image || siteConfig.seo.ogImage,
    datePublished: article.datePublished,
    dateModified: article.dateModified || article.datePublished,
    author: { '@type': 'Person', '@id': `${baseUrl}/#person` },
    publisher: { '@type': 'Organization', '@id': `${baseUrl}/#organization` },
    mainEntityOfPage: { '@type': 'WebPage', '@id': `${currentUrl}#webpage` },
    inLanguage: currentLang,
    isAccessibleForFree: true,
    articleSection: 'Blog',
  } : null;

  // 7. ProfilePage Schema
  const profilePageSchema = type === 'profile' ? {
    '@type': 'ProfilePage',
    '@id': `${currentUrl}#profilepage`,
    mainEntity: { '@type': 'Person', '@id': `${baseUrl}/#person` },
    dateCreated: '2024-01-01',
    dateModified: new Date().toISOString().split('T')[0],
  } : null;

  const schemas = [
    organizationSchema,
    personSchema,
    webSiteSchema,
    webPageSchema,
    breadcrumbSchema,
    articleSchema,
    profilePageSchema,
  ].filter(Boolean);

  const graphSchema = {
    '@context': 'https://schema.org',
    '@graph': schemas,
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(graphSchema, null, 0) }}
    />
  );
}

/**
 * Helper function to generate breadcrumb items
 */
export function generateBreadcrumbs(
  items: Array<{ label: string; path: string }>,
  lang: 'en' | 'zh' = 'en'
): BreadcrumbItem[] {
  const baseUrl = siteConfig.url;
  const langPrefix = lang === 'zh' ? '/zh' : '';

  return [
    { name: lang === 'zh' ? '首页' : 'Home', url: `${baseUrl}${langPrefix}` },
    ...items.map(item => ({
      name: item.label,
      url: `${baseUrl}${langPrefix}${item.path}`,
    })),
  ];
}

/**
 * FAQ Schema generator for FAQ pages
 */
export function generateFAQSchema(faqs: Array<{ question: string; answer: string }>) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: { '@type': 'Answer', text: faq.answer },
    })),
  };
}

/**
 * HowTo Schema generator for tutorial/guide content
 */
export function generateHowToSchema(
  name: string,
  description: string,
  steps: Array<{ name: string; text: string; image?: string }>
) {
  return {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name,
    description,
    step: steps.map((step, index) => ({
      '@type': 'HowToStep',
      position: index + 1,
      name: step.name,
      text: step.text,
      ...(step.image && { image: step.image }),
    })),
  };
}
